import { WithClassname } from '../../../../types';
import React, { useMemo, useState } from 'react';
import useOngoingOrder from '../../../../hooks/context/useOngoingOrder';
import { Alert, Button, Flex, Input } from 'antd';
import { CouponType } from '../../../../types/constants';
import classnames from 'classnames';
import { CloseCircleOutlined } from '@ant-design/icons';
import { getPromoCodeDetails } from '../../../../services/api/CouponService';
import useStore from '../../../../hooks/context/useStore';
import { AxiosError, AxiosResponse } from 'axios';
import { RenderableResponse, ResponseStatus } from '../../../../services/api/client';
import useNotification from '../../../../hooks/context/useNotification';
import useOrderBasket from '../../../../hooks/context/useOrderBasket';
import useHelpers from '../../../../hooks/utility/useHelpers';
import usePromoCode from '../../../../hooks/coupon/usePromoCode';
import PublicPromoCode from '../../../coupon/PublicPromoCode';

export default function PromoCodeInput({ className }: WithClassname) {
  const { storeId } = useStore();
  const { orderData, updateOrderData } = useOngoingOrder();
  const { subtotal } = useOrderBasket();
  const { getCurrentComponent } = usePromoCode();

  const { formatCurrency } = useHelpers();
  const { notificationApi } = useNotification();

  const isApplied = useMemo(
    () => orderData.coupon_type === CouponType.PromoCode,
    [orderData.coupon_type],
  );
  const currentComponent = useMemo(
    () =>
      orderData.coupon_type === CouponType.PromoCode
        ? getCurrentComponent(orderData.coupon_details)
        : null,
    [orderData.coupon_type, orderData.coupon_details],
  );
  const isOrderDiscountable = useMemo(() => {
    if (orderData.coupon_type !== CouponType.PromoCode) {
      return false;
    }

    return currentComponent && subtotal >= currentComponent.usage_threshold;
  }, [orderData.coupon_type, currentComponent, subtotal]);

  const [inputValue, setInputValue] = useState(isApplied ? orderData.coupon_details.code : '');
  const [loading, setLoading] = useState(false);

  const onPromoCodeApply = (code: string) => {
    setInputValue(code);
    setLoading(true);
    const onError = (error: AxiosError) => {
      const handlers = {
        [ResponseStatus.RenderableError]: ({ data }: RenderableResponse) =>
          notificationApi.error({
            message: data.title,
            description: data.description,
          }),
        [ResponseStatus.RenderableErrorTwo]: ({ data }: RenderableResponse) =>
          notificationApi.error({
            message: data.title,
            description: data.description,
          }),
      };

      return (
        handlers[error.status] ??
        ((response: AxiosResponse) => console.error('Failed to create order', { response }))
      )(error.response);
    };

    getPromoCodeDetails(storeId, code, {
      user_phone_number: orderData.customer.phone_number,
    })
      .then(({ data }) =>
        updateOrderData({
          coupon_type: CouponType.PromoCode,
          coupon_details: data,
        }),
      )
      .catch((error) => onError(error))
      .finally(() => setLoading(false));
  };

  const onPromoCodeRemove = () =>
    updateOrderData({
      coupon_type: null,
      coupon_details: null,
    });

  return (
    <div className={className}>
      <Flex className="gap-sm">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.toUpperCase())}
          placeholder="Promo code"
          disabled={isApplied}
          suffix={
            !isApplied && (
              <button
                onClick={() => setInputValue('')}
                className={classnames({
                  hidden: inputValue.length === 0,
                })}
              >
                <CloseCircleOutlined />
              </button>
            )
          }
          size="large"
        />
        <Button
          variant="solid"
          color={isApplied ? 'danger' : 'default'}
          size="large"
          onClick={() => (isApplied ? onPromoCodeRemove() : onPromoCodeApply(inputValue))}
          loading={loading}
          disabled={inputValue.length === 0}
        >
          {isApplied ? 'Remove' : 'Apply'}
        </Button>
      </Flex>
      {isApplied && !isOrderDiscountable && (
        <Alert
          type="warning"
          className="mt-sm"
          message={`Minimum basket amount for this promo is ${formatCurrency(currentComponent.usage_threshold)} (excl. delivery costs)`}
        />
      )}
      {!isApplied && <PublicPromoCode onPromoCodeApply={onPromoCodeApply} className="mt-sm" />}
    </div>
  );
}
